import { Component, OnDestroy, OnInit, ElementRef, ViewChild, ViewEncapsulation, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, ParamMap, NavigationEnd } from '@angular/router';
import { DatePipe, formatDate, formatNumber } from '@angular/common';
import { Title } from "@angular/platform-browser";
import { tap, Observable, forkJoin, of, Subscription, filter, finalize, throwError, Subject } from 'rxjs';
import { map, concatMap, catchError, takeUntil } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { MenuItem, MessageService } from 'primeng/api';
import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard'
import { Client, CustomerCategory } from 'src/app/shared/client';
import { Person } from 'src/app/shared/person';
import { ClientApiService } from 'src/app/services/client-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { CompanyRegisterApplyEvent } from 'src/app/visualcomponents/company-register-verify/company-register-verify.component';
import { CompanyDirectorQuickSetComponent } from 'src/app/visualcomponents/company-director-quick-set/company-director-quick-set.component';
import { ListClientPersonsComponent } from 'src/app/visualcomponents/list-client-persons/list-client-persons.component';
import { AgentPasteToCreateComponent } from 'src/app/visualcomponents/agent-paste-to-create/agent-paste-to-create.component';
import { AgentQuickCreateResult } from 'src/app/visualcomponents/agent-customer-quick-create/agent-customer-quick-create.component';
import { parseRegisterAddress } from 'src/app/shared/register-data-parsing';

@Component({
  selector: 'app-edit-client',
  templateUrl: './edit-client.component.html',
  styleUrls: ['./edit-client.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Organisation (client / counterparty) editor.
 *
 * Loading is split into two methods:
 * - `loadClientContentObservable()` — returns an `Observable` used by `ngOnInit` via a
 *   `concatMap` chain from `route.paramMap`. After loading the client it runs a `forkJoin`
 *   for category (`getCustomerCategoryByID`), persons (`getPersonsByClientId`), and
 *   departments (`getDepartmentsByClientId`) in parallel. `finalize` triggers
 *   `setupIntersectionObserver` with a 100 ms delay.
 * - `loadClientContent()` — the older subscription-based twin, kept alongside the observable
 *   version for backwards compatibility with callers that expect a side-effect invocation.
 *
 * `IntersectionObserver` tracks which of the eight scrollable sections is visible and updates
 * the browser title and navigation menu item states accordingly.
 *
 * Eight tab-visibility flags (`tabCliInfoSelected`, `tabCliPersonSelected`, …) control whether
 * lazy-loaded tab content is rendered. `routeByParameters` activates the right flag from the
 * URL segment and scrolls to the matching anchor.
 *
 * `canDeactivate` blocks navigation both when `clientDoesNotChanged` is false AND when
 * `_clientPutQuery` is still open (i.e. a save is in flight).
 *
 * `onConfirm(1)` POSTs (CLIID == -2) or PUTs. On PUT it conditionally calls
 * `putCustomerBusinessAreaByID` if `CUSTOMERBUISNESSAREA` changed.
 */
export class EditClientComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  /*private*/ _cliId: number;

  globalSettinsData: GlobalSettingsData = null;
  public userAccessGroup: number = null;

  client: Client = null;
  departments: Client[] = null;
  selectedDepartment: Client = null;
  persons: Person[] = null;
  selectedPerson: Person = null;
  childCompany: Client[] = null;
  clientCategory: CustomerCategory = null;

  clientCopy: Client = null;
  selectedDepartmentCopy: Client = null;
  selectedPersonCopy: Person = null;

  /** Director/CEO/owner name from the most recent company-register lookup
   *  (AG-23c), passed down to `app-company-director-quick-set`. Not part of
   *  `Client` — never sent to the API itself, only used to drive that
   *  widget's create/update offer. */
  pendingDirectorName: string | null = null;

  /** `true` when no client property has changed since the last save. */
  public get clientDoesNotChanged(): boolean {
    let equal: boolean = true;

    if (this.clientCopy && this.client) {

    for (var props in this.client) {
      //console.log('*** EditProductComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])
      equal = (this.client[props] == this.clientCopy[props]);
      if (!equal) break;
    }
    }
    return equal;
  }

  /** `true` when no selected-person property has changed since the last save. */
  public get personDoesNotChanged(): boolean {
    let equal: boolean = true;

    for (var props in this.selectedPerson) {
      //console.log('*** EditProductComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])
      equal = (this.selectedPerson[props] == this.selectedPersonCopy[props]);
      if (!equal) break;
    }
    return equal;
  }

  /** `true` when no selected-department property has changed since the last save. */
  public get departmentDoesNotChanged(): boolean {
    let equal: boolean = true;

    for (var props in this.selectedDepartment) {
      //console.log('*** EditProductComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])
      equal = (this.selectedDepartmentCopy[props] == this.selectedDepartmentCopy[props]);
      if (!equal) break;
    }
    return equal;
  }

  /** Returns `true` when the given client property differs from its copy (used by template). */
  public hasChangedProperty(prop: string): boolean {
    return (this.client && this.clientCopy) ? (this.client[prop] != this.clientCopy[prop]) : false;
  }

  private handleError(operation: string) {
    return (error: any) => {
      this.messageService.add({
        severity: 'error',
        summary: `${operation} Failed`,
        detail: error.message || 'Unknown error'
      });
      return throwError(() => error);
    };
  }

  private _initialQuery: Subscription;
  private _clientQuery: Subscription;
  private _personQuery: Subscription;
  private _departmentQuery: Subscription;

  private _clientPutQuery: Subscription;

  tabCliInfoSelected: boolean = true;
  tabCliPersonSelected: boolean = false;
  tabCliDepartmentSelected: boolean = false;
  tabCliLcoSelected: boolean = false;
  tabCliBalanceSelected: boolean = false;
  tabAgentTierSelected: boolean = false;
  tabListProductsOfCustomerSelected: boolean = false;
  tabListProductCustomerTransactionsSelected: boolean = false;
  tabCliDocumentsSelected: boolean = false;

  tabRouter: any[] = [];

  selectedSection: string = 'company';
  baseTitle: string = 'Редактирование организации';


  navigationMenu: MenuItem[] = [{
    label: 'Навигация по Организации',
    icon: 'pi pi-home',
    command: () => { },
    enabled: true,
    disabled: false,
    items: []
  }];

  swapLeftToRight: boolean = true;

  fullURL: string = window.location.href;
  baseURL: string = window.location.href;
  hostname: string = window.location.hostname;
  origin: string = window.location.origin;

  @ViewChild('anchorMain') anchorMain?: ElementRef;
  @ViewChild('anchorPersons') anchorPersons?: ElementRef;
  @ViewChild('anchorDepartments') anchorDepartments?: ElementRef;
  @ViewChild('anchorChild') anchorChild?: ElementRef;
  @ViewChild('anchorBalance') anchorBalance?: ElementRef;
  @ViewChild('anchorProducts1') anchorProducts1?: ElementRef;
  @ViewChild('anchorProducts2') anchorProducts2?: ElementRef;
  @ViewChild('anchorDocuments') anchorDocuments?: ElementRef;
  @ViewChild('anchorClientTier') anchorClientTier?: ElementRef;

  /** Used by onRegisterVerifyApply() to trigger the director create/update
   *  from the same click as the register-verify "Оновити" -- one confirm,
   *  two calls (Clients then Person), instead of the manager needing a
   *  second click on this widget's own buttons. */
  @ViewChild(CompanyDirectorQuickSetComponent) directorQuickSet?: CompanyDirectorQuickSetComponent;

  /** Only present while the "Сотрудники" accordion tab has been opened at
   *  least once and stays open -- that tab's `[cache]="false"` destroys
   *  its content on collapse, so this reference can legitimately be
   *  undefined. `onDirectorPersonChanged()` below already null-checks it;
   *  if the tab is closed there's nothing visible to patch, and opening it
   *  fresh already re-fetches current data on its own. */
  @ViewChild(ListClientPersonsComponent) listClientPersons?: ListClientPersonsComponent;

  /** AG-25 paste-to-create dialog reference. */
  @ViewChild('pasteToCreateDialog') pasteToCreateDialog?: AgentPasteToCreateComponent;

  /** `true` when the nickname is auto-set from the category name and must not be edited directly. */
  public get disallowEditNickname(): boolean {

    return (this.client && this.clientCategory && (this.client.CLIID != -2) && (this.client.CLIENTSNIKNAME == this.clientCategory.CASTCATNAME));
  }
  private destroy$ = new Subject<void>();
  clientInfoIsLoading: boolean = false;

  /** Current logo as a data URL for `<img>` display. Null when no logo. */
  logoDataUrl: string | null = null;

  /** Current stamp as a data URL for `<img>` display. Null when no stamp. */
  stampDataUrl: string | null = null;

  /** True while logo upload/delete is in progress. */
  logoUploading: boolean = false;

  /** True while stamp upload/delete is in progress. */
  stampUploading: boolean = false;

  private intersectionObserver: IntersectionObserver;
  private currentVisibleSection: string = 'company';
  private elementToRouteMap: Map<Element, any> = new Map();

  constructor(
    private cliApi: ClientApiService,
    private restapi: RestApiService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
    private cdr: ChangeDetectorRef
  ) {
    const [hash, page, dt, did, sec] = window.location.hash.split('/');

    this.baseURL = window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + '/' + hash + '/' + page + '/' + dt + '/' + did + '/';

    this.tabRouter = [
      { label: 'Общая информация', icon: 'pi pi-fw  pi-home', anchor: () => { return this.anchorMain; }, section: 'company', activateTab: () => { this.tabCliInfoSelected = true; } },
      { label: 'Сотрудники', icon: 'pi pi-fw pi-users', anchor: () => { return this.anchorPersons; }, section: 'users', activateTab: () => { this.tabCliPersonSelected = true; } },
      { label: 'Склады и отделы', icon: 'pi pi-fw pi-truck', anchor: () => { return this.anchorDepartments; }, section: 'departments', activateTab: () => { this.tabCliDepartmentSelected = true; } },
      { label: 'Юридические лица', icon: 'pi pi-fw pi-home', anchor: () => { return this.anchorChild; }, section: 'children', activateTab: () => { this.tabCliLcoSelected = true; } },
      { label: 'Баланс с покупателем', icon: 'pi pi-fw pi-credit-card', anchor: () => { return this.anchorBalance; }, section: 'balance', activateTab: () => { this.tabCliBalanceSelected = true; } },
      { label: 'Все товары контрагента', icon: 'pi pi-fw pi-truck', anchor: () => { return this.anchorProducts1; }, section: 'products', activateTab: () => { this.tabListProductsOfCustomerSelected = true; } },
      { label: 'Движение товаров за период', icon: 'pi pi-fw pi-calendar', anchor: () => { return this.anchorProducts2; }, section: 'transactions', activateTab: () => { this.tabListProductCustomerTransactionsSelected = true; } },
      { label: 'Все документы контрагента', icon: 'pi pi-fw pi-wallet', anchor: () => { return this.anchorDocuments; }, section: 'documents', activateTab: () => { this.tabCliDocumentsSelected = true; } },
      { label: 'Рівень обслуговування клієнта', icon: 'pi pi-fw pi-user-edit', anchor: () => { return this.anchorClientTier; }, section: 'tier', activateTab: () => { this.tabAgentTierSelected = true; } },
    ];

    this.navigationMenu[0].items = this.tabRouter.map(e => {
      return {
        label: e.label,
        icon: e.icon,
        command: () => { this.router.navigate([page, this._cliId, e.section]) },
        enabled: true,
        disabled: false,
        //url: '/',
        //routerLink: [page, this._docId, this._docType, e.section],
      }
    });
  }

  /**
   * Guards navigation — blocks when there are unsaved changes or when a save query is still open.
   * Also fires on browser refresh/close via `@HostListener('window:beforeunload')`.
   */
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.clientDoesNotChanged && this._clientPutQuery && !this._clientPutQuery.closed) {
      return false; // Block navigation during save
    }
    return this.clientDoesNotChanged;
  }

  private setupIntersectionObserver(): void {
    console.log('*** EditClientComponent in setupIntersectionObserver');
    
    // Disconnect existing observer if any
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    // Clear and rebuild the map
    this.elementToRouteMap.clear();
    
    // First pass: build the element-to-route mapping
    this.tabRouter.forEach(route => {
      const anchorElement = route.anchor();
      console.log('*** Checking route:', route.section, 'anchorElement:', anchorElement);
      if (anchorElement?.nativeElement) {
        this.elementToRouteMap.set(anchorElement.nativeElement, route);
        console.log('*** Mapped element for section:', route.section, anchorElement.nativeElement);
      } else {
        console.warn('*** No anchor element found for section:', route.section);
      }
    });

    console.log('*** Total mapped elements:', this.elementToRouteMap.size);

    if (this.elementToRouteMap.size === 0) {
      console.warn('*** No elements to observe, will retry in 500ms');
      setTimeout(() => this.setupIntersectionObserver(), 500);
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px 0px -50% 0px',
      threshold: [0, 0.1, 0.5, 1]
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      environment.production || console.log('*** IntersectionObserver callback, entries count:', entries.length);
      
      entries.forEach(entry => {
        environment.production || console.log('*** Entry:', {
          isIntersecting: entry.isIntersecting,
          target: entry.target,
          ratio: entry.intersectionRatio
        });
        
        if (entry.isIntersecting) {
          const route = this.elementToRouteMap.get(entry.target);
          environment.production || console.log('*** Found route:', route?.section);
          
          if (route && this.currentVisibleSection !== route.section) {
            environment.production || console.log('*** Updating section from', this.currentVisibleSection, 'to', route.section);
            this.currentVisibleSection = route.section;
            this.updateTitleAndNavigation(route);
          }
        }
      });
    }, options);

    // Second pass: observe all elements
    this.elementToRouteMap.forEach((route, element) => {
      this.intersectionObserver.observe(element);
      environment.production || console.log('*** Observing element for section:', route.section);
    });
  }

  private updateTitleAndNavigation(route: any): void {
    if (!this.client) return;

    environment.production || console.log('*** EditClientComponent section changed to:', route.section);

    this.selectedSection = route.section;
    this.titleService.setTitle(`${this.baseTitle}: ${this.client.CLIENTSNIKNAME.trim()}: ${route.label}`);

    this.navigationMenu[0].items.forEach(item => {
      item.disabled = (item.label === route.label);
    });
  }

  /** Disconnects the IntersectionObserver, signals `destroy$`, and unsubscribes all queries. */
  ngOnDestroy(): void {
    console.log('*** EditClientComponent in ngOnDestroy')

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.destroy$.next();
    this.destroy$.complete();

    if (this._initialQuery) { this._initialQuery.unsubscribe(); }
    if (this._clientQuery) { this._clientQuery.unsubscribe(); }
    if (this._personQuery) { this._personQuery.unsubscribe(); }
    if (this._departmentQuery) { this._departmentQuery.unsubscribe(); }
    if (this._clientPutQuery) { this._clientPutQuery.unsubscribe(); }
  }
  /** Reads global settings and subscribes to route params, calling `loadClientContentObservable` on each navigation. */
  ngOnInit(): void {
    console.log('*** EditClientComponent in ngOnInit')
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();
    this.userAccessGroup = this.globalSettinsData.accessGroup;
    
    this._initialQuery =
      this.route.paramMap.pipe(
        takeUntil(this.destroy$),

        concatMap((params: ParamMap) => {
          this._cliId = +params.get('id');
          environment.production || console.log('*** EditClientComponent in param subscribe: (' + this._cliId + ') ' + JSON.stringify(params));
          if (params.get('section')) {
            environment.production || console.log('*** EditClientComponent in param subscribe: (' + this._cliId + ') section: ' + JSON.stringify(params.get('section')));
          }

          return this.loadClientContentObservable().pipe(
            map(() => params),
            tap(p => {
              environment.production || console.log('*** EditClientComponent in param subscribe: (' + this._cliId + ') before routing: ' + JSON.stringify(p));
            }),

            map((p => {
              const sec = p ? p.get('section') : this.selectedSection;
              environment.production || console.log('*** EditClientComponent in param subscribe: (' + this._cliId + ') section: ' + sec);
              this.routeByParameters(sec);
            })
          ));
        }),
      )
      .subscribe({
        next: () => {
          environment.production || console.log('*** EditClientComponent in param subscribe: completed');
        },
        error: (err) => {
          console.error('Error in route paramMap subscription:', err);
        }
      });

    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      //      this.baseURL = this.hostname + '/edit-document/' + this._docType + '/' + this._docId + '/';

      environment.production || console.log('*** EditClientComponent in NavigationEnd: ' + event.url);
      environment.production || console.log('*** EditClientComponent in NavigationEnd: ' + this.baseURL);
    });
  }

  /** Activates the tab for the given section name and scrolls to its anchor element. */
  routeByParameters(selectedSection: string) {
    // environment.production || console.log('*** EditClientComponent  in routeByParameters: tabRouter: ' + JSON.stringify(this.tabRouter));
    console.log('*** EditClientComponent in routeByParameters: selectedSection: ' + selectedSection);

    let route = this.tabRouter.find(s => s.section == selectedSection);
    // environment.production || console.log('*** EditClientComponent  in routeByParameters: found section: ' + JSON.stringify(route));
    if (route) {
      environment.production || console.log('*** EditClientComponent in routeByParameters: route: ' + JSON.stringify(route));

      route.activateTab();
      if (this.client) {
        this.titleService.setTitle(this.baseTitle + ': ' + this.client.CLIENTSNIKNAME.trim() + route.label ? ': ' + route.label : '');
      }
      // Scroll to the anchor element after a short delay to ensure the view is ready
      setTimeout(() => {
        if (route.anchor()) {
          route.anchor().nativeElement.scrollIntoView({ behavior: 'smooth' });
          this.selectedSection = selectedSection;

          this.navigationMenu[0].items.forEach(item => {
            item.disabled = (item.label === route.label);
          });

        }
      }, 500);
    }
  }

  /**
   * Returns an Observable that loads the client, then runs a `forkJoin` for category,
   * persons, and departments. `finalize` sets `clientInfoIsLoading = false` and schedules
   * `setupIntersectionObserver` with a 100 ms delay. New clients (id 0) are created in-memory.
   */
  loadClientContentObservable(): Observable<any> {
    console.log('*** EditClientComponent loadClientContentObservable: ===> Started');
    this.clientInfoIsLoading = true;

    const createNewClient = (): Client => ({
      CLIID: -2,
      CLISUBSOF: null,
      CLICATEGORY: 0,
      CLIENTSNIKNAME: 'Новая организация',
      CLIENTSNAME: '',
      CLIENTCOUNTRY: '',
      CLIENTCITY: '',
      CLIENTADDR1: '',
      CLIENTADR2: null,
      CLIENTZIP: '',
      CLIENTBANK: null,
      CLIENTACCOUNT: null,
      CLIENTREGCODE: null,
      CLIENTVATREGCODE: null,
      CLIENTBANKREGCODE: null,
      CLIENTBANKCODE: null,
      CLEINTBANKCITY: null,
      CLIENTBANKADDR1: null,
      CLENTBANKADDR2: null,
      CLIENTBANKZIP: null,
      CLIENTBANKPHONE: null,
      CLIENTNOTE1: null,
      CLIENTNOTE2: null,
      CLIENTVATREGNUM: null,
      CLILOGOID: null,
      CASTCATID: 0,
      CURID: null,
      CLIDOCINDEX: null,
      CLISTAMPID: null,
      CLIENTBANK2ACC: null
    });

    return (this._cliId ?
      this.cliApi.getClientById(this._cliId).pipe(
        map(cli => cli?.length > 0 ? cli[0] : null),

        tap(cli => {
          if (!cli) {
            console.log('*** EditClientComponent loadClientContentObservable: ===> No Data Loaded');
          }
        }),

        catchError(error => {
          console.error('Error loading client:', error);
          return throwError(() => error);
        })
       )
      :
      of(createNewClient())
    ).pipe(
      tap(cli => {
        console.log('*** EditClientComponent loadClientContentObservable: ===> DONE');
        this.client = cli;
        this.clientCopy = { ...this.client };

        const title = this._cliId && cli ? `${this.baseTitle} ${cli.CLIENTSNIKNAME.trim()}` : 'Создание новой организации';
        this.titleService.setTitle(title);
      }),

      concatMap(cli => forkJoin(
        {
          clicat: this.cliApi.getCustomerCategoryByID(cli.CASTCATID).pipe(
            map(data => data[0]),
            catchError(err => {
              this.handleError('Load Customer Category Info')(err);
              return of(null);
            })
          ),
          cliper: this.cliApi.getPersonsByClientId(cli.CLIID).pipe(
            map(data => data),
            catchError(err => {
              this.handleError('Load Person Info')(err);
              return of(null);
            })
          ),
          clidep: this.cliApi.getDepartmentsByClientId(cli.CLIID).pipe(
            map(data => data),
            catchError(err => {
              this.handleError('Load Department Info')(err);
              return of(null);
            })
          )
        }
      )),

      // All parallel requests complete here
      tap(({ clicat, cliper, clidep }) => {
        this.clientCategory = clicat;
        this.persons = cliper;
        this.departments = clidep;
      }),

      catchError(error => {
        console.error('Error loading client:', error);
        this.titleService.setTitle(`${this.baseTitle}: Ошибка загрузки`); 
        return throwError(() => error);
      }),

      finalize(() => {
        this.clientInfoIsLoading = false;
        this.loadClientImages();

        // Set up IntersectionObserver after data is loaded and view is updated
        setTimeout(() => {
          this.setupIntersectionObserver();
        }, 100);
      })
    );
  }

  /**
   * Subscription-based twin of `loadClientContentObservable`. Kept for compatibility with
   * callers that invoke it as a side-effect rather than subscribing to the returned Observable.
   */
  loadClientContent() {
    console.log('*** EditClientComponent loadClientContent: ===> Started');
    this.clientInfoIsLoading = true;
    if (this._cliId) {
      console.log('*** EditClientComponent loadClientContent _cliId: ' + this._cliId);

      this._clientQuery =
        this.cliApi.getClientById(this._cliId).pipe(
          takeUntil(this.destroy$), // Auto-unsubscribe on destroy

          filter(cli => cli?.length > 0),
          map(cli => this.client = cli[0]),

          concatMap(cli => forkJoin(
            {
              clicat: this.cliApi.getCustomerCategoryByID(cli.CASTCATID).pipe(
                map(data => data[0]),
                catchError(err => {
                  this.handleError('Load Customer Category Info')(err);
                  return of(null);
                })
              ),
              cliper: this.cliApi.getPersonsByClientId(cli.CLIID).pipe(
                map(data => data),
                catchError(err => {
                  this.handleError('Load Person Info')(err);
                  return of(null);
                })
              ),
              clidep: this.cliApi.getDepartmentsByClientId(cli.CLIID).pipe(
                map(data => data),
                catchError(err => {
                  this.handleError('Load Department Info')(err);
                  return of(null);
                })
              )
            }
          )),
        // All parallel requests complete here
          tap(({ clicat, cliper, clidep }) => {
            this.clientCategory = clicat;
            this.persons = cliper;
            this.departments = clidep;
          }),
          catchError(error => {
            console.error('Error loading client:', error);
            return throwError(() => error);
          }),
        )
        .subscribe({
          next: () => {
            console.log('*** EditClientComponent loadClientContent: ===> DONE');
            if (this.client) {
              this.clientCopy = { ... this.client };
              this.clientInfoIsLoading = false;
              this.titleService.setTitle(`${this.baseTitle} ${this.client.CLIENTSNIKNAME.trim()}`);
            } else {
              this.titleService.setTitle(`${this.baseTitle} *** EditClientComponent loadClientContent: ===> Something went wrong: no Data Loaded`); 
              this.clientInfoIsLoading = false;
              console.log('*** EditClientComponent loadClientContent: ===> Something went wrong: no Data Loaded');
            }
          },
          error: (err) => {
            console.error('Error loading client:', err);
            this.clientInfoIsLoading = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load client data'
            })
          }
        });

    } else {
      this.client = {
        "CLIID": -2,
        "CLISUBSOF": null,
        "CLICATEGORY": 0,
        "CLIENTSNIKNAME": "Новая организация",
        "CLIENTSNAME": "",
        "CLIENTCOUNTRY": "",
        "CLIENTCITY": "",
        "CLIENTADDR1": "",
        "CLIENTADR2": null,
        "CLIENTZIP": "",
        "CLIENTBANK": null,
        "CLIENTACCOUNT": null,
        "CLIENTREGCODE": null,
        "CLIENTVATREGCODE": null,
        "CLIENTBANKREGCODE": null,
        "CLIENTBANKCODE": null,
        "CLEINTBANKCITY": null,
        "CLIENTBANKADDR1": null,
        "CLENTBANKADDR2": null,
        "CLIENTBANKZIP": null,
        "CLIENTBANKPHONE": null,
        "CLIENTNOTE1": null,
        "CLIENTNOTE2": null,
        "CLIENTVATREGNUM": null,
        "CLILOGOID": null,
        "CASTCATID": 0,
        "CURID": null,
        "CLIDOCINDEX": null,
        "CLISTAMPID": null,
        "CLIENTBANK2ACC": null
      }; 
      this.clientCopy = { ... this.client };
      this.titleService.setTitle(`${this.baseTitle} Создание новой организации`);
      this.clientInfoIsLoading = false;
    }
    console.log('*** EditClientComponent loadClientContent: ===> Ended');
  }

  /** Stores the selected department row into `selectedDepartment`. */
  onDepartmentRowSelect(event) {
    console.log('*** EditClientComponent onDepartmentRowSelect: ' + JSON.stringify(event));

    this.selectedDepartment = event.data;
    //if (this.selectedDocumentId) {
    //  //this.doLoadDataItems(this.selectedDocumentId);
    //}
  }

  /** No-op handler for department row unselection. */
  onDepartmentRowUnselect(event) { }

  /** Stores the selected person row into `selectedPerson`. */
  onPersonRowSelect(event) {
    console.log('*** EditClientComponent onPersonRowSelect: ' + JSON.stringify(event.data));

    this.selectedPerson = event.data;
    //if (this.selectedDocumentId) {
    //  //this.doLoadDataItems(this.selectedDocumentId);
    //}
  }

  /** No-op handler for person row unselection. */
  onPersonRowUnselect(event) { }

  /** Applies the selected currency ID (`CURID`) from the currency picker. */
  cs1Selected(currency) {
    console.log('*** EditClientComponent cs1Selected: ' + JSON.stringify(currency))
    if (currency && 'CURID' in currency) {
      this.client.CURID = currency.CURID;
    }
  }

  /** Applies the selected customer-category ID (`CASTCATID`) from the category picker. */
  cs2Selected(castCatId) {
    console.log('*** EditClientComponent cs2Selected: ' + castCatId)
    this.client.CASTCATID = castCatId;
  }

  /** Activates lazy-loaded tab content for accordion tabs 4–7 when first opened. */
  onTabOpen(event) {
    console.log('*** EditClientComponent in onTabOpen: ' + event.index);

    switch (event.index) {
      case 1:
        break;

      case 2:
        break;

      case 3:
        break;

      case 4:
        this.tabCliBalanceSelected = true;
        break;

      case 5:   // Count index of Accordion from 0
        this.tabListProductsOfCustomerSelected = true;
        break;

      case 6:   // Count index of Accordion from 0
        this.tabListProductCustomerTransactionsSelected = true;
        break;

      case 7:   // Count index of Accordion from 0
        this.tabCliDocumentsSelected = true;
        break;

      default:
        break;
    }
  }

  /** Debug-only handler that logs client vs. copy (does not trigger a save). */
  buttonSaveAllClick(event) {
    console.log('*** EditClientComponent buttonSaveAllClick: ' + JSON.stringify(event))
    console.log('*** EditClientComponent buttonSaveAllClick: client ' + JSON.stringify(this.client))
    console.log('*** EditClientComponent buttonSaveAllClick: clientCopy ' + JSON.stringify(this.clientCopy))
  }

  /**
   * Applies a confirmed company-register lookup result (AG-23c/AG-23d) onto
   * the client form. `event.scope` — chosen via the register-verify modal's
   * split button — decides which half runs: `'company'`/`'all'` apply
   * `CLIENTSNAME` and the parsed address; `'person'`/`'all'` stage the
   * director name and drive `company-director-quick-set`. `CLIENTSNIKNAME`
   * (short name) now also gets applied when the register actually returned
   * one (`result.shortName`) — corrected 2026-09-09, reversing the AG-23c
   * design doc's original "leave it alone" default: that default assumed
   * there was no reliable short-name source to apply, but youcontrol's
   * response already carries a real `name.shortName` distinct from the
   * full legal name (e.g. official "ПРИВАТНЕ АКЦІОНЕРНЕ ТОВАРИСТВО
   * НАУКОВО-ТЕХНІЧНИЙ ЦЕНТР \"ІНФОРМАЦІЙНІ СИСТЕМИ\"" vs. short "ПРАТ НТЦ
   * \"ІНФОРМСИСТЕМИ\"") — `youcontrol-provider.ts` has parsed it into
   * `CompanyRegisterResult.shortName` since AG-23a, this function just
   * never read it. Same non-destructive pattern as every other field here:
   * only overwritten when the register actually provided a value.
   * `disallowEditNickname`'s own input-level lock only affects manual
   * typing (see `edit-client.component.html`'s `[disabled]` binding) — it
   * does not gate this programmatic apply, same as it never gated
   * `CLIENTSNAME` above. The manager still has to click "Сохранить" to
   * persist the *Client* fields; this only fills the form.
   *
   * Address parsing (AG-23d): `parseRegisterAddress()` splits the
   * register's single-line address into `CLIENTZIP`/`CLIENTCOUNTRY`/
   * `CLIENTCITY`/`CLIENTADDR1`/`CLIENTADR2` when the string matches the
   * confirmed comma-delimited shape; otherwise it falls back to the
   * pre-AG-23d behavior (the whole string into `CLIENTADDR1`) rather than
   * guess at an unconfirmed format.
   *
   * One confirm, two calls (PR #149): for `'all'`/`'person'`, this same
   * click also drives `Person` directly via `confirmPending()`. Setting
   * `pendingDirectorName` only updates `directorQuickSet`'s `@Input()` --
   * Angular doesn't run its `ngOnChanges()` (and therefore doesn't
   * recompute its `state`/`confidence`) until the next change-detection
   * pass, so `detectChanges()` is forced here before reading
   * `directorQuickSet`, otherwise `confirmPending()` would act on the
   * *previous* state. `confirmPending()` itself (AG-23d) only auto-writes
   * when the match was unambiguous -- an uncertain match leaves its own
   * `'picker'` state rendered, waiting for a separate explicit confirm.
   */
  onRegisterVerifyApply(event: CompanyRegisterApplyEvent): void {
    if (!this.client) { return; }
    const { result, vatInfo, taxationMode, scope } = event;

    if (scope === 'all' || scope === 'company') {
      if (result.officialName) { this.client.CLIENTSNAME = result.officialName; }
      if (result.shortName) { this.client.CLIENTSNIKNAME = result.shortName; }
      if (result.legalAddress) {
        const parsed = parseRegisterAddress(result.legalAddress);
        if (parsed) {
          if (parsed.zip) { this.client.CLIENTZIP = parsed.zip; }
          if (parsed.country) { this.client.CLIENTCOUNTRY = parsed.country; }
          if (parsed.city) { this.client.CLIENTCITY = parsed.city; }
          if (parsed.addr1) { this.client.CLIENTADDR1 = parsed.addr1; }
          if (parsed.addr2) { this.client.CLIENTADR2 = parsed.addr2; }
        } else {
          this.client.CLIENTADDR1 = result.legalAddress;
        }
      }

      // VAT/tax-payer data (owner spec, 2026-08-31, date source corrected
      // 2026-09-01): CLIENTVATREGCODE gets the vendor's individual tax
      // number verbatim; CLIENTVATREGNUM is a composed human-readable
      // string ("<status> (<date>)") since that field has no single
      // confirmed vendor source of its own. CLIENTNOTE2 gets an audit
      // stamp for this update, appended after whatever was already there
      // rather than overwriting it.
      //
      // Built from registrationDate/cancellationDate, NEVER actualDate --
      // originally composed from actualDate (youcontrol's "as of" field),
      // but the owner caught it live twice (2026-08-31, 2026-09-01, two
      // different real companies) always showing today's/near-today's date
      // instead of a real freshness marker, even with showCurrentData
      // forced on. actualDate just isn't a usable "as of" date for this
      // vendor in practice -- registrationDate is the stable, real fact,
      // and it's what opendatabot's own composition already used (that
      // vendor never had an actualDate field to begin with).
      // CLIENTNOTE2 fragments from this one apply click -- the update stamp
      // and the taxation-mode line -- are collected here and joined onto a
      // SINGLE new line with a single space (owner correction, 2026-09-01:
      // previously each was appended as its own \n line via two separate
      // reads/writes of CLIENTNOTE2, producing two lines instead of one).
      // Still appended after whatever CLIENTNOTE2 already held, on its own
      // \n line -- only the join between these two fragments changed.
      const noteFragments: string[] = [];

      if (vatInfo?.found) {
        if (vatInfo.vatNumber) { this.client.CLIENTVATREGCODE = vatInfo.vatNumber; }

        const dateSource = vatInfo.cancellationDate || vatInfo.registrationDate;
        const datePart = dateSource ? formatDate(dateSource, 'dd.MM.yyyy', 'en-US') : null;
        const statusWord = vatInfo.cancellationDate ? 'Скасовано' : (vatInfo.note?.trim() || 'Зареєстровано');
        const vatRegNum = datePart ? `${statusWord} (${datePart})` : (vatInfo.note?.trim() || null);
        if (vatRegNum) { this.client.CLIENTVATREGNUM = vatRegNum; }

        noteFragments.push(`Оновлено з ${result.provider} ${formatDate(new Date(), 'dd.MM.yyyy', 'en-US')}`);
      }

      // Taxation mode (owner spec, 2026-09-01): no dedicated Clients field
      // exists for this, so it's folded into the same CLIENTNOTE2 line as
      // the update stamp above rather than overwriting anything.
      if (taxationMode?.found) {
        if (taxationMode.generalSystem === true) {
          noteFragments.push('Система оподаткування: загальна');
        } else if (taxationMode.singleTaxGroup != null) {
          const rateText = taxationMode.singleTaxRate != null ? `, ${taxationMode.singleTaxRate}%` : '';
          noteFragments.push(`Система оподаткування: єдиний податок, гр.${taxationMode.singleTaxGroup}${rateText}`);
        }
      }

      if (noteFragments.length) {
        const combined = noteFragments.join(' ');
        const existingNote = this.client.CLIENTNOTE2?.trim();
        this.client.CLIENTNOTE2 = existingNote ? `${existingNote}\n${combined}` : combined;
      }
    }

    if (scope === 'all' || scope === 'person') {
      this.pendingDirectorName = result.director || null;
      this.cdr.detectChanges();
      this.directorQuickSet?.confirmPending();
    }
  }

  /**
   * `company-director-quick-set` (AG-23d) emits the created/updated
   * `Person` directly via `(personChanged)` after every successful write —
   * this patches both places on this screen that hold a persons copy,
   * single-row, without a server round-trip:
   * - `this.persons`, so a later register-verify click's `recompute()`
   *   sees the fresh data instead of re-offering a stale create/update.
   * - the separate "Сотрудники" tab (`ListClientPersonsComponent`, which
   *   loads its own copy independently via `[clientId]` and has no
   *   `[personList]` input of its own) via `upsertPerson()`, so it reflects
   *   the change immediately rather than staying stale until the manager
   *   clicks "Обновить" or reopens the tab.
   */
  onDirectorPersonChanged(person: Person): void {
    const index = (this.persons || []).findIndex(p => p.PERSID === person.PERSID);
    this.persons = (index >= 0)
      ? this.persons.map((p, i) => (i === index ? person : p))
      : [...(this.persons || []), person];

    this.listClientPersons?.upsertPerson(person);
  }

  /** AG-25: Opens the paste-to-create dialog in the appropriate mode. */
  onOpenPasteToCreate(): void {
    if (!this.client) { return; }
    if (!this.pasteToCreateDialog) { return; }

    // In new-company mode (CLIID === -2), open in 'new-company' mode
    // In existing-company mode, open in 'new-contact' mode to add a contact
    const isNewCompany = this.client.CLIID === -2;
    this.pasteToCreateDialog.mode = isNewCompany ? 'new-company' : 'new-contact';
    this.pasteToCreateDialog.cliId = isNewCompany ? null : this.client.CLIID;
    this.pasteToCreateDialog.open();
  }

  /** Called when AgentPasteToCreateComponent emits a successful create result. */
  onPasteToCreateCompleted(result: AgentQuickCreateResult): void {
    console.log('*** EditClientComponent onPasteToCreateCompleted:', result);

    // If a new company was created (new-company mode), navigate to the newly created company
    // to replace the currently blank new-client form.
    if (result.cliId !== null && this.client?.CLIID === -2) {
      this.router.navigate(['/edit-client', result.cliId]);
    }
  }

  /**
   * Saves the client. `e === 1` POSTs (CLIID == -2) and routes to the new ID, or PUTs and
   * conditionally calls `putCustomerBusinessAreaByID` when the business area changed.
   */
  onConfirm(e: number) {
    console.log('*** EditClientComponent onConfirm: ' + e)

    switch (e) {
      case 1:
        console.log('*** EditClientComponent onConfirm: (Main data) ');
        console.log('*** EditClientComponent buttonSave: client ' + JSON.stringify(this.client));
        console.log('*** EditClientComponent buttonSave: clientCopy ' + JSON.stringify(this.clientCopy));

        if (this.client.CLIID == -2) {
          this._clientPutQuery =
              this.cliApi.postClientById(this.client)
                .pipe(

                  map(data => {
                    console.log('*** EditClientComponent onConfirm: post ' + JSON.stringify(data));
                    this._cliId = data['CLIID'];
                    this.clientCopy = { ... this.client }; // Stash all changes to allow smooth route
                  }),

                  catchError(error => {
                    console.error('Error savingclient:', error);
                    this.messageService.add({
                      severity: 'error',
                      summary: 'Error',
                      detail: 'Failed to save client data'
                    });
                    return of(null); // Return fallback value
                  }),
              )
            .subscribe({
              next: () => {
                if (this._cliId > 0) {
                  this.router.navigate(['/edit-client', this._cliId]);
                }
              },
              error: (err) => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Save Failed',
                  detail: 'Could not create client'
                });
              }
            });
        } else {
          this._clientPutQuery =
              this.cliApi.putClientById(this.client).pipe(
                tap(cli => console.log('*** EditClientComponent onConfirm: saved ' + JSON.stringify(cli))),

                filter(() => this.clientCopy.CUSTOMERBUISNESSAREA != this.client.CUSTOMERBUISNESSAREA),

                concatMap(cli => this.cliApi.putCustomerBusinessAreaByID(this.client)),

                catchError(error => {
                  console.error('Error savingclient:', error);
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to save client data'
                  });
                  return of(null); // Return fallback value
                }),

                finalize(() => this.clientCopy = { ... this.client })
              )
              .subscribe(() => console.log('*** EditClientComponent onConfirm: saved '));
        }

        break;

      case 2:
        console.log('*** EditClientComponent 2');
        break;

      case 3:
        console.log('*** EditClientComponent 3');
        break;

      case 4:
        break;

      default:
        break;
    }
  }

  /** Opens all four primary section tabs simultaneously. */
  openAllTabs(event) {
    this.tabCliInfoSelected = true;
    this.tabCliPersonSelected = true;
    this.tabCliDepartmentSelected = true;
    this.tabCliLcoSelected = true;
  }

  /** Shows the company-info tab and hides all sibling tabs. */
  openCompanyInfoTab(event) {
    this.tabCliInfoSelected = true;
    this.tabCliPersonSelected = false;
    this.tabCliDepartmentSelected = false;
    this.tabCliLcoSelected = false;
  }

  /** Shows the persons tab and hides all sibling tabs. */
  openPersonsTab(event) {
    this.tabCliInfoSelected = false;
    this.tabCliPersonSelected = true;
    this.tabCliDepartmentSelected = false;
    this.tabCliLcoSelected = false;
  }

  /** Shows the departments tab and hides all sibling tabs. */
  openDepartmentsTab(event) {
    this.tabCliInfoSelected = false;
    this.tabCliPersonSelected = false;
    this.tabCliDepartmentSelected = true;
    this.tabCliLcoSelected = false;
  }

  /** Shows the legal-entities (LCO) tab and hides all sibling tabs. */
  openLcosTab(event) {
    this.tabCliInfoSelected = false;
    this.tabCliPersonSelected = false;
    this.tabCliDepartmentSelected = false;
    this.tabCliLcoSelected = true;
  }

  /** Navigates to the client editor in new-client mode (id 0). */
  onCreateNewClient(event) {
    console.log('*** EditClientComponent onCreateNewClient: ' + JSON.stringify(event))
    this.router.navigate(['/edit-client', 0]);
  }

  /** Applies the selected business-area string to `client.CUSTOMERBUISNESSAREA`. */
  onselectedBusinessArea(event) {
    console.log('*** EditClientComponent onselectedBusinessArea: ' + JSON.stringify(event))

    this.client.CUSTOMERBUISNESSAREA = event;
  }

  /** Sets `tabListProductsOfCustomerSelected = true` when the product-list tab is opened. */
  onTabListProductOpen(event) {
    console.log('*** EditClientComponent onTabListProductOpen: ' + JSON.stringify(event))
    this.tabListProductsOfCustomerSelected = true;
  }

  /** Sets `tabListProductsOfCustomerSelected = false` when the product-list tab is closed. */
  onTabListProductClose(event) {
    console.log('*** EditClientComponent onTabListProductClose: ' + JSON.stringify(event))
    this.tabListProductsOfCustomerSelected = false;
  }

  private loadClientImages(): void {
    this.logoDataUrl = null;
    this.stampDataUrl = null;
    if (!this.client) { return; }

    if (this.client.CLILOGOID) {
      this.cliApi.getImageById(this.client.CLILOGOID)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: img => { this.logoDataUrl = 'data:' + img.IMAGENOTE + ';base64,' + img.IMAGEDATA; },
          error: () => { this.logoDataUrl = null; }
        });
    }

    if (this.client.CLISTAMPID) {
      this.cliApi.getImageById(this.client.CLISTAMPID)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: img => { this.stampDataUrl = 'data:' + img.IMAGENOTE + ';base64,' + img.IMAGEDATA; },
          error: () => { this.stampDataUrl = null; }
        });
    }
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this._cliId || this._cliId <= 0) { return; }
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const [header, base64] = dataUrl.split(',');
      const mimeType = header.match(/:(.*?);/)?.[1] ?? file.type;
      this.logoUploading = true;
      this.cliApi.uploadClientLogo(this._cliId, mimeType, base64)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: result => {
            this.client.CLILOGOID = result.CLILOGOID;
            this.logoDataUrl = dataUrl;
            this.logoUploading = false;
          },
          error: () => { this.logoUploading = false; }
        });
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onLogoDelete(): void {
    if (!this._cliId || !this.client?.CLILOGOID) { return; }
    this.logoUploading = true;
    this.cliApi.deleteClientLogo(this._cliId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.client.CLILOGOID = null; this.logoDataUrl = null; this.logoUploading = false; },
        error: () => { this.logoUploading = false; }
      });
  }

  onStampFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this._cliId || this._cliId <= 0) { return; }
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const [header, base64] = dataUrl.split(',');
      const mimeType = header.match(/:(.*?);/)?.[1] ?? file.type;
      this.stampUploading = true;
      this.cliApi.uploadClientStamp(this._cliId, mimeType, base64)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: result => {
            this.client.CLISTAMPID = result.CLISTAMPID;
            this.stampDataUrl = dataUrl;
            this.stampUploading = false;
          },
          error: () => { this.stampUploading = false; }
        });
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onStampDelete(): void {
    if (!this._cliId || !this.client?.CLISTAMPID) { return; }
    this.stampUploading = true;
    this.cliApi.deleteClientStamp(this._cliId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.client.CLISTAMPID = null; this.stampDataUrl = null; this.stampUploading = false; },
        error: () => { this.stampUploading = false; }
      });
  }
}
